import { useCallback, useContext, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { PlusCircle } from 'react-feather';
import { ChatSessionFiltersContext } from '@trinity/chat-components/components/Chat/context';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { PrimaryButton } from '@trinity/chat-components/components/presentation/Button.Primary';
import { SecondaryButton } from '@trinity/chat-components/components/presentation/Button.Secondary';
import { useTheme } from '@trinity/chat-components/components/Theme';
import { ChatCustomFilter, type ChatFilterOperator } from '@trinity/chat';
import type { BrandInsightsInstanceContext } from '@trinity/chat';
import type { CustomFilter } from '@trinity/chat';
import { AgentCustomFilterValueSelector } from './AgentCustomFilter.Value';
import { AgentFilterLabelSelect } from './AgentFilter.LabelSelect';
import { CustomFilterOperatorSelect } from './CustomFilter.OperatorSelect';
import { CustomFilterValue as CustomFilterValueDisplay } from './CustomFilter.Value';
import type { AgentFilterItem } from './interfaces';
import * as Presentation from './Presentation';
import type { AgentFilter } from '@trinity/chat';

type Props = {
  context: BrandInsightsInstanceContext.AgentWithMetadata;
  filters?: AgentFilter[];
};

type AgentCustomFilterFormProps = {
  agentIdentifier: string;
  agentFilters: AgentFilterItem[];
  initialFilter?: CustomFilter.AgentCustomFilter;
  onSubmit: (filterId: number, operator: ChatFilterOperator, values: CustomFilter.ItemValue[]) => void;
  onCancel: () => void;
};

function AgentCustomFilterForm({ agentIdentifier, agentFilters, initialFilter, onSubmit, onCancel }: AgentCustomFilterFormProps) {
  const [formState, setFormState] = useState<{
    filterId: number | null;
    operator: ChatFilterOperator | null;
    values: CustomFilter.ItemValue[];
  }>(() => {
    if (initialFilter) {
      const firstValue = initialFilter.value?.[0];
      const filterId = firstValue && 'filterId' in firstValue ? Number(firstValue.filterId) : null;
      const operator = initialFilter.operator?.[0] || null;
      return {
        filterId,
        operator,
        values: initialFilter.value || [],
      };
    }
    return {
      filterId: null,
      operator: null,
      values: [],
    };
  });

  const selectedFilterDef = useMemo(() => {
    return agentFilters.find(f => f.id === formState.filterId);
  }, [agentFilters, formState.filterId]);

  const availableOperators = useMemo(() => {
    if (!selectedFilterDef) return [];
    return selectedFilterDef.operators as ChatFilterOperator[];
  }, [selectedFilterDef]);

  const handleFilterSelect = useCallback((filterId: number) => {
    const filterDef = agentFilters.find(f => f.id === filterId);
    if (!filterDef) return;

    setFormState({
      filterId,
      operator: filterDef.operators[0] as ChatFilterOperator,
      values: [],
    });
  }, [agentFilters]);

  const handleOperatorSelect = useCallback((operator: ChatFilterOperator) => {
    setFormState(prev => ({
      ...prev,
      operator,
    }));
  }, []);

  const handleValuesSelect = useCallback((values: CustomFilter.ItemValue[]) => {
    setFormState(prev => ({
      ...prev,
      values,
    }));
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(() => {
    if (!formState.filterId || !formState.operator || formState.values.length === 0) return;
    onSubmit(formState.filterId, formState.operator, formState.values);
  }, [formState, onSubmit]);

  const canSubmit = useMemo(() => {
    return !!(formState.filterId && formState.operator && formState.values.length > 0);
  }, [formState]);

  return (
    <FilterForm>
      <StyledPresentationRow>
        <StyledLabel>{initialFilter ? 'Edit Filter' : 'New Filter'}</StyledLabel>
        <Presentation.Value>
          <FilterValues>
            <AgentFilterLabelSelect
              value={formState.filterId}
              options={agentFilters}
              onSelect={handleFilterSelect}
            />
            {selectedFilterDef && (
              <CustomFilterOperatorSelect
                options={availableOperators}
                value={formState.operator || availableOperators[0]}
                onSelect={handleOperatorSelect}
              />
            )}
            {selectedFilterDef && formState.operator && (
              <AgentCustomFilterValueSelector
                agentIdentifier={agentIdentifier}
                filter={selectedFilterDef}
                values={formState.values}
                onValuesChange={handleValuesSelect}
              />
            )}
          </FilterValues>
        </Presentation.Value>
      </StyledPresentationRow>

      <Footer>
        <StyledSecondaryButton onClick={onCancel}>
          Cancel
        </StyledSecondaryButton>
        <StyledPrimaryButton
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          {initialFilter ? 'Update' : 'Save'}
        </StyledPrimaryButton>
      </Footer>
    </FilterForm>
  );
}

export function AgentCustomFilters({ context: _context, filters: passedFilters }: Props) {
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const [hasUnsavedAgentFilter, setHasUnsavedAgentFilter] = useState(false);
  const [editingFilterIndex, setEditingFilterIndex] = useState<number | null>(null);

  // Use passed filters or empty array as fallback
  const availableFilters = useMemo(() => {
    if (!passedFilters) return [];

    return passedFilters.map((filter) => ({
      id: filter.id,
      identifier: filter.identifier,
      label: filter.label,
      type: filter.type,
      operators: filter.operators,
      data: filter.data,
    }));
  }, [passedFilters]);

  const activeAgentFilters = useMemo(() => {
    return filters.custom?.filter(f => f.type === ChatCustomFilter.AgentFilter) || [];
  }, [filters.custom]);

  const handleAddAgentFilter = useCallback(() => {
    setHasUnsavedAgentFilter(true);
  }, []);

  const handleRemoveUnsavedFilter = useCallback(() => {
    setHasUnsavedAgentFilter(false);
  }, []);

  const handleSaveAgentFilter = useCallback((filterId: number, operator: ChatFilterOperator, values: CustomFilter.ItemValue[]) => {
    const selectedFilterDef = availableFilters?.find(f => f.id === filterId);
    if (!selectedFilterDef) return;

    const agentFilter: CustomFilter.AgentCustomFilter = {
      type: ChatCustomFilter.AgentFilter,
      operator: [operator],
      value: values.map(v => ({
        ...v,
        filterId: selectedFilterDef.id,
        filterLabel: selectedFilterDef.label,
      })),
    };

    dispatch({
      type: 'filters/add-custom-filter',
      payload: {
        value: agentFilter,
      },
    });
    setHasUnsavedAgentFilter(false);
  }, [availableFilters, dispatch]);

  const handleRemoveFilter = useCallback((index: number) => () => {
    const customFilters = filters.custom || [];
    const agentFilterIndex = customFilters.findIndex((filter, i) =>
      filter.type === ChatCustomFilter.AgentFilter &&
      customFilters.slice(0, i + 1).filter(f => f.type === ChatCustomFilter.AgentFilter).length === index + 1,
    );

    if (agentFilterIndex !== -1) {
      dispatch({
        type: 'filters/remove-custom-filter',
        payload: {
          id: agentFilterIndex,
        },
      });
    }
    setEditingFilterIndex(null);
  }, [filters.custom, dispatch]);

  const handleToggleEdit = useCallback((index: number) => () => {
    setEditingFilterIndex(index);
  }, []);

  const handleSaveEditedFilter = useCallback((filterId: number, operator: ChatFilterOperator, values: CustomFilter.ItemValue[]) => {
    if (editingFilterIndex === null) return;

    const selectedFilterDef = availableFilters?.find(f => f.id === filterId);
    if (!selectedFilterDef) return;

    const customFilters = filters.custom || [];
    const agentFilterIndex = customFilters.findIndex((filter, i) =>
      filter.type === ChatCustomFilter.AgentFilter &&
      customFilters.slice(0, i + 1).filter(f => f.type === ChatCustomFilter.AgentFilter).length === editingFilterIndex + 1,
    );

    if (agentFilterIndex !== -1) {
      const updatedFilter: CustomFilter.AgentCustomFilter = {
        type: ChatCustomFilter.AgentFilter,
        operator: [operator],
        value: values.map(v => ({
          ...v,
          filterId: selectedFilterDef.id,
          filterLabel: selectedFilterDef.label,
        })),
      };

      dispatch({
        type: 'filters/update-custom-filter',
        payload: {
          id: agentFilterIndex,
          value: updatedFilter,
        },
      });
    }
    setEditingFilterIndex(null);
  }, [editingFilterIndex, availableFilters, filters.custom, dispatch]);

  const renderAgentFilter = useCallback((filter: CustomFilter.AgentCustomFilter, index: number) => {
    const isEditing = index === editingFilterIndex;

    if (isEditing) {
      return (
        <AgentCustomFilterForm
          agentIdentifier={_context.data.agent.identifier}
          initialFilter={filter}
          agentFilters={availableFilters || []}
          onSubmit={handleSaveEditedFilter}
          onCancel={() => setEditingFilterIndex(null)}
        />
      );
    }

    const firstValue = filter.value?.[0];
    const filterLabel = firstValue && 'filterLabel' in firstValue
      ? String(firstValue.filterLabel)
      : 'Agent Filter';

    return (
      <Presentation.Row>
        <Presentation.Label>{'Agent Filter'}</Presentation.Label>
        <Presentation.Value>
          <CustomFilterValueDisplay
            value={filter}
            editable={editingFilterIndex === null}
            onEdit={handleToggleEdit(index)}
            onRemove={handleRemoveFilter(index)}
            filterLabel={filterLabel}
          />
        </Presentation.Value>
      </Presentation.Row>
    );
  }, [editingFilterIndex, handleToggleEdit, handleRemoveFilter, _context.data.agent.identifier, availableFilters, handleSaveEditedFilter]);

  if (!passedFilters?.length) {
    return null;
    // return (
    //   <Presentation.Row>
    //     <Presentation.Label>Agent Filters</Presentation.Label>
    //     <Presentation.Value>
    //       <div>No custom filters available for this agent</div>
    //     </Presentation.Value>
    //   </Presentation.Row>
    // );
  }

  return (
    <>
      {/* Render existing agent filters */}
      {activeAgentFilters.map((filter, index) => (
        <CustomFilterRow key={`agent-filter-${index}`}>
          {renderAgentFilter(filter, index)}
        </CustomFilterRow>
      ))}

      {/* Add new filter button */}
      {!hasUnsavedAgentFilter && editingFilterIndex === null && (
        <AddAgentFilterButton onClick={handleAddAgentFilter} />
      )}

      {/* New filter form */}
      {hasUnsavedAgentFilter && (
        <CustomFilterRow>
          <AgentCustomFilterForm
            agentIdentifier={_context.data.agent.identifier}
            agentFilters={availableFilters}
            onSubmit={handleSaveAgentFilter}
            onCancel={handleRemoveUnsavedFilter}
          />
        </CustomFilterRow>
      )}
    </>
  );
}

// Component for add button matching profile page style
function AddAgentFilterButton({ onClick }: { onClick: () => void }) {
  const theme = useTheme();

  return (
    <AddButtonRoot>
      <AddButton onClick={onClick}>
        <StyledPlusCircle color={theme.palette.primary.main} />
        Add Agent Filter
      </AddButton>
    </AddButtonRoot>
  );
}

// Styled components matching FilterPane.tsx and CustomFilter.Form.tsx
const CustomFilterRow = styled.div(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.gray.light1}`,
}));

const buttonSize = 80;

const AddButtonRoot = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 12,
  borderTop: `1px solid ${theme.palette.gray.light1}`,
  width: '100%',
}));

const AddButton = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  color: theme.palette.primary.main,
  fontFamily: theme.fonts.semiBold,
  fontSize: 15,
}));

const StyledPlusCircle = styled(PlusCircle)({
  marginRight: 8,
});

const FilterForm = styled.div({
  marginTop: 10,
});

const StyledPresentationRow = styled(Presentation.Row)({
  alignItems: 'flex-start',
});

const StyledLabel = styled(Presentation.Label)(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  fontSize: 15,
  color: theme.palette.gray.dark1,
}));

const FilterValues = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  flex: 1,
});

const StyledSecondaryButton = styled(SecondaryButton)({
  width: buttonSize,
  minWidth: buttonSize,
  marginRight: 8,
  fontSize: 14,
  padding: 6,
});

const StyledPrimaryButton = styled(PrimaryButton)({
  width: buttonSize,
  minWidth: buttonSize,
  fontSize: 14,
  padding: 6,
});

const Footer = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  marginTop: 10,
});
